import type { ApiResponse } from 'shared/api/responses';
import { api, nullOn404 } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n, type MessageKey } from 'shared/i18n';
import {
  DocumentTaxonomyConfig,
  DocumentTaxonomyConfigTaxonomy,
  DocumentTaxonomyConfigContext,
  DocumentTaxonomyConfigCurrency,
  CreateDocumentTaxonomyConfigInput,
  UpdateDocumentTaxonomyConfigInput,
  AddTaxonomyToConfigInput,
  RemoveTaxonomyFromConfigInput,
  AddContextToConfigInput,
  RemoveContextFromConfigInput,
  SetActiveTaxonomyInput,
  AddCurrencyToConfigInput,
  RemoveCurrencyFromConfigInput,
  SetActiveCurrencyInput,
  SetActiveContextInput,
  SuccessResponse,
  TagCountAttribute,
  TagAttributeCounts,
} from './types';
import { taxonomyQueryKeys } from './taxonomy';
import { tagQueryKeys } from './keys';

const showSuccessToast = (messageKey: MessageKey) => {
  dispatchMessage(EventType.ADD_MESSAGE, { type: 'success', text: i18n(messageKey), removeTime: 4000 });
};

export const documentTaxonomyConfigQueryKeys = {
  configByDocument: 'DOC_TAXONOMY_CONFIG_BY_DOCUMENT',
  tagCounts: 'DOC_TAXONOMY_CONFIG_TAG_COUNTS',
} as const;

export function useDocumentTaxonomyConfig(documentSuperId: number | undefined): UseQueryResult<DocumentTaxonomyConfig | null> {
  return useQuery({
    queryKey: [documentTaxonomyConfigQueryKeys.configByDocument, documentSuperId],

    queryFn: () =>
      api
        .GET('/document-taxonomy-config/document/{documentSuperId}', { params: { path: { documentSuperId: String(documentSuperId) } } })
        // 204 (no config for this document) is typed as "" by the contract — same null as a 404.
        .then((res) => res.data || null)
        .catch(nullOn404),

    enabled: !!documentSuperId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useCreateDocumentTaxonomyConfig(): UseMutationResult<void, unknown, CreateDocumentTaxonomyConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDocumentTaxonomyConfigInput) => {
      await api.POST('/document-taxonomy-config/', { body: input });
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument, variables.documentSuperId],
      });
    },
  });
}

export function useUpdateDocumentTaxonomyConfig(): UseMutationResult<SuccessResponse, unknown, UpdateDocumentTaxonomyConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, silent: _silent, ...configData }: UpdateDocumentTaxonomyConfigInput) => {
      const res = await api.PUT('/document-taxonomy-config/{configId}', { params: { path: { configId } }, body: configData });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      if (!variables.silent) showSuccessToast('tag:config.updated.success');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useDeleteDocumentTaxonomyConfig(): UseMutationResult<SuccessResponse, unknown, { configId: string; documentSuperId?: number }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId }: { configId: string; documentSuperId?: number }) => {
      const res = await api.DELETE('/document-taxonomy-config/{configId}', { params: { path: { configId } } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument, variables.documentSuperId],
      });
    },
  });
}

export function useAddTaxonomyToConfig(): UseMutationResult<DocumentTaxonomyConfigTaxonomy, unknown, AddTaxonomyToConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, taxonomyId }: AddTaxonomyToConfigInput) => {
      const res = await api.POST('/document-taxonomy-config/{configId}/taxonomies', { params: { path: { configId } }, body: { taxonomyId } });
      return res?.data as DocumentTaxonomyConfigTaxonomy;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.taxonomy.added');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.activeTaxonomy],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.presentationTree],
      });
    },
  });
}

type RemoveTaxonomyResult = ApiResponse<'/document-taxonomy-config/{configId}/taxonomies/{taxonomyId}', 'delete'>;

export function useRemoveTaxonomyFromConfig(): UseMutationResult<RemoveTaxonomyResult, unknown, RemoveTaxonomyFromConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, taxonomyId }: RemoveTaxonomyFromConfigInput) => {
      const res = await api.DELETE('/document-taxonomy-config/{configId}/taxonomies/{taxonomyId}', { params: { path: { configId, taxonomyId } } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.taxonomy.removed');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.tagCounts],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.activeTaxonomy],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.presentationTree],
      });
      queryClient.invalidateQueries({
        queryKey: [tagQueryKeys.tagsByDocument],
      });
      queryClient.invalidateQueries({
        queryKey: [tagQueryKeys.tagsByElement],
      });
    },
  });
}

export function useAddContextToConfig(): UseMutationResult<DocumentTaxonomyConfigContext, unknown, AddContextToConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, contextId }: AddContextToConfigInput) => {
      const res = await api.POST('/document-taxonomy-config/{configId}/contexts', { params: { path: { configId } }, body: { contextId } });
      return res?.data as DocumentTaxonomyConfigContext;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.date.added');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useRemoveContextFromConfig(): UseMutationResult<SuccessResponse, unknown, RemoveContextFromConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, contextId }: RemoveContextFromConfigInput) => {
      const res = await api.DELETE('/document-taxonomy-config/{configId}/contexts/{contextId}', { params: { path: { configId, contextId } } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.date.removed');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useSetActiveTaxonomy(): UseMutationResult<SuccessResponse, unknown, SetActiveTaxonomyInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, taxonomyId }: SetActiveTaxonomyInput) => {
      const res = await api.PUT('/document-taxonomy-config/{configId}/active-taxonomy', { params: { path: { configId } }, body: { taxonomyId } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.taxonomy.active.updated');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.activeTaxonomy],
      });
      queryClient.invalidateQueries({
        queryKey: [taxonomyQueryKeys.presentationTree],
      });
    },
  });
}

export function useAddCurrencyToConfig(): UseMutationResult<DocumentTaxonomyConfigCurrency, unknown, AddCurrencyToConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, currency }: AddCurrencyToConfigInput) => {
      const res = await api.POST('/document-taxonomy-config/{configId}/currencies', { params: { path: { configId } }, body: { currency } });
      return res?.data as DocumentTaxonomyConfigCurrency;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.currency.added');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useRemoveCurrencyFromConfig(): UseMutationResult<SuccessResponse, unknown, RemoveCurrencyFromConfigInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, currencyId }: RemoveCurrencyFromConfigInput) => {
      const res = await api.DELETE('/document-taxonomy-config/{configId}/currencies/{currencyId}', { params: { path: { configId, currencyId } } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.currency.removed');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useSetActiveCurrency(): UseMutationResult<SuccessResponse, unknown, SetActiveCurrencyInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, currencyId }: SetActiveCurrencyInput) => {
      const res = await api.PUT('/document-taxonomy-config/{configId}/active-currency', { params: { path: { configId } }, body: { currencyId } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.currency.active.updated');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useSetActiveContext(): UseMutationResult<SuccessResponse, unknown, SetActiveContextInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, contextId }: SetActiveContextInput) => {
      const res = await api.PUT('/document-taxonomy-config/{configId}/active-context', { params: { path: { configId } }, body: { contextId } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      showSuccessToast('tag:config.date.active.updated');
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useTagAttributeCounts(configId: string | undefined, attributes: TagCountAttribute[]): UseQueryResult<TagAttributeCounts> {
  const attributesKey = attributes.sort().join(',');
  return useQuery({
    queryKey: [documentTaxonomyConfigQueryKeys.tagCounts, configId, attributesKey],

    queryFn: async () => {
      const res = await api.GET('/document-taxonomy-config/{configId}/tag-counts', {
        params: { path: { configId: configId as string }, query: { attributes: attributesKey } },
      });
      return res?.data || {};
    },

    enabled: !!configId && attributes.length > 0,
  });
}
